import CartIconSvg from 'assets/icon-cart';
import useBrandToggleFeature from 'components/BrandToggleFeature/use-brand-toggle-feature';
import useCustomHistory from 'hooks/use-custom-history';
import useCartItems from 'hooks/useCartItems';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ExperienceCountBadge } from './ExperienceCountBadge';

export interface IExperienceFilterParam {
  categoryIds?: string;
  vibeIds?: string;
  startDate?: string;
  endDate?: string;
  lang?: string;
}

interface CartIconProps {
  searchParam?: IExperienceFilterParam;
}

export const CartIcon = ({ searchParam }: CartIconProps) => {
  const [hovered, setHovered] = useState(false);
  const isCartEnabled = useBrandToggleFeature('shoppingCart');
  const { cartItems } = useCartItems();
  const itemsInCart = Object.values(cartItems).length;
  const { push } = useCustomHistory();
  const { t } = useTranslation();

  if (!isCartEnabled) {
    return null;
  }

  const goToCart = () => {
    if (itemsInCart >= 1) {
      push({
        pathname: '/checkout',
        search: searchParam ? `?${searchParam}` : '',
      });
    }
  };

  const ariaLabel = `${t('viewShoppingCart')} - ${t('itemsInCart', {
    count: itemsInCart,
  })}`;

  const isEmpty = itemsInCart < 1;

  // Empty cart reads as inactive: muted icon + border (alongside the existing 0.6 opacity and
  // not-allowed cursor). Hover-darkening is gated on a non-empty cart.
  const iconColor = (() => {
    if (isEmpty) return 'var(--way-colors-contrastColorShades-30)';
    if (hovered) return 'var(--way-colors-contrastColorShades-100)';
    return 'var(--way-colors-contrastColorShades-70)';
  })();

  return (
    <IconWrapper
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      disabled={isEmpty}
      aria-disabled={isEmpty}
      className="header__cart-button"
      onKeyDown={(e) => e.key === 'Enter' && goToCart()}
      onMouseOver={() => setHovered(true)}
      onMouseOut={() => setHovered(false)}
      onClick={goToCart}
    >
      <ExperienceCountBadge count={itemsInCart} />
      <CartIconSvg
        scale={1.3}
        color={iconColor}
        className="header__cart-button-icon"
      />
    </IconWrapper>
  );
};

const IconWrapper = styled.div<{
  disabled: boolean;
}>`
  position: relative;
  grid-area: cart;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  border-radius: var(--way-design-borderRadiusDefault);
  border: 0.5px solid var(--way-colors-borderColor);
  opacity: ${({ disabled }) => (disabled ? '0.6' : '1')};
  height: 50px;
  width: 50px;
  gap: 8px;

  &:hover {
    border: 0.5px solid
      ${({ disabled }) =>
        disabled
          ? 'var(--way-colors-borderColor)'
          : 'var(--way-colors-borderHoverColor)'};
  }
`;
